// src/dashboard/pages/teacher/Palmares/utils/palmaresDashboardUtils.js

export const DEFAULT_AVATAR = "/img/user_avatar.jpg";

const API_BASE = process.env.REACT_APP_API_URL || "";

export function isExternalUrl(url) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

export function toAbsoluteUrl(url) {
  if (!url) return "";

  const value = String(url).trim();

  if (!value) return "";
  if (isExternalUrl(value)) return value;
  if (value.startsWith("data:") || value.startsWith("blob:")) return value;

  if (!value.startsWith("/uploads")) {
    return value;
  }

  const cleanBase = API_BASE.replace(/\/$/, "");
  return `${cleanBase}${value}`;
}

export function getAvatarSrc(photoUrl) {
  return toAbsoluteUrl(photoUrl) || DEFAULT_AVATAR;
}

export function normalizeString(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

export function formatDateFr(date) {
  if (!date) return "—";

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "—";

  return parsed.toLocaleDateString("fr-FR");
}

export function toDateInputValue(date) {
  if (!date) return "";

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "";

  return parsed.toISOString().slice(0, 10);
}

export function getAdherentFromPalmares(palmares) {
  return palmares?.adherent || palmares?.Adherent || palmares?.member || {};
}

export function getEventFromPalmares(palmares) {
  return palmares?.event || palmares?.Event || palmares?.evenement || {};
}

export function getMemberDisplayName(member) {
  const nom = String(member?.nom || "").trim();
  const prenom = String(member?.prenom || "").trim();

  const fullName = `${nom.toUpperCase()} ${prenom}`.trim();

  return fullName || "Adhérent non renseigné";
}

export function getPalmaresMemberName(palmares) {
  return getMemberDisplayName(getAdherentFromPalmares(palmares));
}

export function getEventTitle(event) {
  return (
    event?.titre_event ||
    event?.titre ||
    event?.title ||
    event?.nom ||
    event?.name ||
    event?.event_nom ||
    event?.label ||
    ""
  );
}

export function getEventDate(event) {
  return (
    event?.date_event ||
    event?.date ||
    event?.jour ||
    event?.datetime ||
    event?.start ||
    event?.start_date ||
    event?.startDate ||
    ""
  );
}

export function getEventPlace(event) {
  return (
    event?.lieu_event ||
    event?.lieu ||
    event?.ville ||
    event?.location ||
    event?.adresse ||
    ""
  );
}

export function formatEventLabel(event) {
  if (!event) return "—";

  const title = getEventTitle(event);
  const date = formatDateFr(getEventDate(event));
  const place = getEventPlace(event);

  if (!title && event?.id) {
    return `Événement #${event.id}`;
  }

  return [title || "Événement", date !== "—" ? date : "", place]
    .filter(Boolean)
    .join(" — ");
}

export function sortMembers(members = []) {
  return [...members].sort((a, b) =>
    getMemberDisplayName(a).localeCompare(getMemberDisplayName(b), "fr", {
      sensitivity: "base",
    }),
  );
}

export function sortEvents(events = []) {
  return [...events].sort((a, b) => {
    const dateA = new Date(getEventDate(a));
    const dateB = new Date(getEventDate(b));

    const timeA = Number.isNaN(dateA.getTime()) ? 0 : dateA.getTime();
    const timeB = Number.isNaN(dateB.getTime()) ? 0 : dateB.getTime();

    return timeB - timeA;
  });
}

export function getPreviousSeason(season) {
  const [startYear, endYear] = String(season || "")
    .split("-")
    .map(Number);

  if (!startYear || !endYear) return "";

  return `${startYear - 1}-${endYear - 1}`;
}

export function getNextSeason(season) {
  const [startYear, endYear] = String(season || "")
    .split("-")
    .map(Number);

  if (!startYear || !endYear) return "";

  return `${startYear + 1}-${endYear + 1}`;
}

export function getSeasonFromDate(date) {
  if (!date) return "";

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "";

  const year = parsed.getFullYear();
  const month = parsed.getMonth();

  if (month >= 7) {
    return `${year}-${year + 1}`;
  }

  return `${year - 1}-${year}`;
}

export function isWithinSeason(date, season) {
  if (!season) return true;

  const [startYear, endYear] = String(season).split("-").map(Number);

  if (!startYear || !endYear) return true;

  const seasonStart = new Date(`${startYear}-08-01T00:00:00`);
  const seasonEnd = new Date(`${endYear}-07-31T23:59:59`);
  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) return false;

  return parsedDate >= seasonStart && parsedDate <= seasonEnd;
}

export function getActiveSeasonFromSettings(settingsResponse) {
  const settings = Array.isArray(settingsResponse)
    ? settingsResponse[0]
    : settingsResponse;

  return settings?.saison_active || "";
}

export function getSeasonOptions(palmares = [], activeSeason = "") {
  const seasons = new Set();

  if (activeSeason) {
    seasons.add(getPreviousSeason(activeSeason));
    seasons.add(activeSeason);
    seasons.add(getNextSeason(activeSeason));
  }

  palmares.forEach((item) => {
    const season = getSeasonFromDate(item?.date_palmares);
    if (season) seasons.add(season);
  });

  return [...seasons].filter(Boolean).sort((a, b) => {
    const startA = Number(String(a).split("-")[0]);
    const startB = Number(String(b).split("-")[0]);
    return startB - startA;
  });
}

export function sortPalmares(items = [], sort = "date_desc") {
  return [...items].sort((a, b) => {
    if (sort === "date_asc") {
      return new Date(a?.date_palmares || 0) - new Date(b?.date_palmares || 0);
    }

    if (sort === "member_asc") {
      return getPalmaresMemberName(a).localeCompare(
        getPalmaresMemberName(b),
        "fr",
        {
          sensitivity: "base",
        },
      );
    }

    if (sort === "member_desc") {
      return getPalmaresMemberName(b).localeCompare(
        getPalmaresMemberName(a),
        "fr",
        {
          sensitivity: "base",
        },
      );
    }

    if (sort === "title_asc") {
      return String(a?.titre_palmares || "").localeCompare(
        String(b?.titre_palmares || ""),
        "fr",
        { sensitivity: "base" },
      );
    }

    return new Date(b?.date_palmares || 0) - new Date(a?.date_palmares || 0);
  });
}

export function filterPalmares(items = [], filters = {}) {
  const query = normalizeString(filters.query);
  const memberId = String(filters.memberId || "");
  const season = filters.season || "";
  const visibility = filters.visibility || "";

  const filtered = items.filter((item) => {
    const member = getAdherentFromPalmares(item);
    const event = getEventFromPalmares(item);

    const currentMemberId =
      item?.adherent_palmares_id ||
      member?.id ||
      item?.adherent_id ||
      item?.AdherentId;

    const searchable = normalizeString(
      [
        getMemberDisplayName(member),
        item?.titre_palmares,
        item?.resultats_palmares,
        item?.classement_palmares,
        item?.observations_palmares,
        formatEventLabel(event),
      ]
        .filter(Boolean)
        .join(" "),
    );

    const matchesQuery = !query || searchable.includes(query);

    const matchesMember =
      !memberId || String(currentMemberId) === String(memberId);

    const matchesSeason =
      !season || isWithinSeason(item?.date_palmares, season);

    const matchesVisibility =
      !visibility ||
      (visibility === "active" && !!item?.is_active) ||
      (visibility === "hidden" && !item?.is_active);

    return matchesQuery && matchesMember && matchesSeason && matchesVisibility;
  });

  return sortPalmares(filtered, filters.sort);
}

export function calculatePalmaresStats(
  allItems = [],
  filteredItems = [],
  activeSeason = "",
) {
  const activeItems = allItems.filter((item) => !!item?.is_active);
  const hiddenItems = allItems.filter((item) => !item?.is_active);
  const rankedItems = allItems.filter((item) =>
    String(item?.classement_palmares || "").trim(),
  );

  const currentSeasonItems = activeSeason
    ? allItems.filter((item) =>
        isWithinSeason(item?.date_palmares, activeSeason),
      )
    : [];

  return {
    total: allItems.length,
    filtered: filteredItems.length,
    active: activeItems.length,
    hidden: hiddenItems.length,
    ranked: rankedItems.length,
    currentSeason: currentSeasonItems.length,
  };
}

export function buildPalmaresPayload(formData = {}, options = {}) {
  const { includeAdherent = false } = options;

  const payload = {
    event_palmares_id: formData.event_palmares_id
      ? Number(formData.event_palmares_id)
      : null,
    date_palmares: formData.date_palmares || null,
    titre_palmares: String(formData.titre_palmares || "").trim(),
    resultats_palmares: String(formData.resultats_palmares || "").trim(),
    classement_palmares: String(formData.classement_palmares || "").trim(),
    observations_palmares: String(formData.observations_palmares || "").trim(),
    is_active: !!formData.is_active,
  };

  if (includeAdherent) {
    payload.adherent_palmares_id = formData.adherent_palmares_id
      ? Number(formData.adherent_palmares_id)
      : null;
  }

  return payload;
}

export function getResolvedEventFromPalmares(palmares, events = []) {
  const directEvent = getEventFromPalmares(palmares);

  if (directEvent && Object.keys(directEvent).length > 0) {
    const hasTitle =
      getEventTitle(directEvent) ||
      directEvent?.titre_event ||
      directEvent?.titre ||
      directEvent?.title ||
      directEvent?.nom ||
      directEvent?.name;

    if (hasTitle) {
      return directEvent;
    }
  }

  const eventId =
    palmares?.event_palmares_id ||
    palmares?.event_id ||
    palmares?.EventId ||
    palmares?.eventPalmaresId;

  if (!eventId) return null;

  return (
    events.find((event) => String(event.id) === String(eventId)) || {
      id: eventId,
    }
  );
}

export function getPalmaresMemberOptions(palmares = []) {
  const map = new Map();

  palmares.forEach((item) => {
    const member = getAdherentFromPalmares(item);
    const memberId =
      item?.adherent_palmares_id ||
      member?.id ||
      item?.adherent_id ||
      item?.AdherentId;

    if (!memberId) return;

    if (!map.has(String(memberId))) {
      map.set(String(memberId), {
        id: String(memberId),
        label: getMemberDisplayName(member),
      });
    }
  });

  return [...map.values()].sort((a, b) =>
    a.label.localeCompare(b.label, "fr", { sensitivity: "base" }),
  );
}
